import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Print as PrintIcon, Receipt as ReceiptIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { facturaPosPath, ROUTES } from '../../config/routes';
import ordenReparacionService, { OrdenReparacionReadDto } from '../../services/ordenReparacionService';
import PDFViewer from '../../components/shared/PDFViewer';
import { getApiErrorMessage } from '../../utils/apiError';
import Can from '../../components/Can';
import {
  idOrdenTrabajoApi,
  labelEstadoOrdenTrabajo,
  nroFormularioOrdenTrabajo,
  ordenTrabajoEsEditable,
  ordenTrabajoEstaFacturada,
  puedeFacturarOrdenTrabajo,
} from '../../utils/ordenTrabajoFactura';

const ESTADOS_OT = ['Pendiente', 'Facturada', 'Anulada'] as const;
type EstadoOtFiltro = (typeof ESTADOS_OT)[number] | '';

const formatFecha = (value?: string | null): string => {
  if (!value) return '—';
  const s = String(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y}`;
};

const chipColorEstado = (orden: OrdenReparacionReadDto): 'success' | 'default' | 'warning' => {
  if (ordenTrabajoEstaFacturada(orden)) return 'success';
  const estado = String(orden.estado ?? '').trim().toLowerCase();
  if (estado.includes('pend')) return 'warning';
  return 'default';
};

const fieldSx = {
  mb: 0,
  '& .MuiInputBase-root': { minHeight: 48, fontSize: '1.05rem' },
  '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#f8f9fa' },
};

const btnSx = (bgColor: string, hoverColor: string) => ({
  bgcolor: bgColor,
  color: '#fff',
  borderRadius: 1.5,
  width: 32,
  height: 32,
  '&:hover': { bgcolor: hoverColor, transform: 'scale(1.1)' },
  '&:disabled': { bgcolor: '#ccc' },
  transition: 'all 0.2s',
});

const OrdenesReparacion: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const abortRef = useRef<AbortController | null>(null);
  const [ordenes, setOrdenes] = useState<OrdenReparacionReadDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<EstadoOtFiltro>('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfTitle, setPdfTitle] = useState('Orden de trabajo');
  const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null);

  const goNuevo = useCallback(() => {
    navigate(ROUTES.servicio.ordenesTrabajo.nuevo);
  }, [navigate]);

  const fetchOrdenes = useCallback(
    async (
      pageNum: number,
      pageSize: number,
      search: string,
      estado: EstadoOtFiltro,
      desde: string,
      hasta: string,
    ) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        setLoading(true);
        const data = await ordenReparacionService.getOrdenes({
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: search || undefined,
          estado: estado || undefined,
          fechaDesde: desde || undefined,
          fechaHasta: hasta || undefined,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setOrdenes(data.items);
        setTotalCount(data.totalCount);
      } catch (error: unknown) {
        if (controller.signal.aborted) return;
        enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar órdenes'), { variant: 'error' });
        setOrdenes([]);
        setTotalCount(0);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    const t = setTimeout(
      () => fetchOrdenes(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta),
      searchTerm ? 300 : 0,
    );
    return () => {
      clearTimeout(t);
      abortRef.current?.abort();
    };
  }, [page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, fetchOrdenes]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Insert' && e.key !== 'F2') return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) {
        if (e.key === 'F2') return;
      }
      e.preventDefault();
      goNuevo();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goNuevo]);

  const handleFacturar = (orden: OrdenReparacionReadDto, event: React.MouseEvent) => {
    event.stopPropagation();
    const nroFormulario = nroFormularioOrdenTrabajo(orden);
    if (!nroFormulario) {
      enqueueSnackbar('La orden no tiene número de formulario para facturar', { variant: 'warning' });
      return;
    }
    navigate(facturaPosPath({ ordenTrabajoId: nroFormulario }));
  };

  const handleImprimir = async (orden: OrdenReparacionReadDto, event: React.MouseEvent) => {
    event.stopPropagation();
    const apiId = idOrdenTrabajoApi(orden);
    if (!apiId) {
      enqueueSnackbar('La orden no tiene identificador válido', { variant: 'warning' });
      return;
    }
    try {
      setPdfLoadingId(apiId);
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
      const blob = await ordenReparacionService.getPdf(apiId);
      setPdfTitle(`Orden ${orden.serie} ${orden.numero}`.trim());
      setPdfUrl(window.URL.createObjectURL(blob));
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al generar el PDF'), { variant: 'error' });
    } finally {
      setPdfLoadingId(null);
    }
  };

  const handleDelete = async (orden: OrdenReparacionReadDto) => {
    if (!ordenTrabajoEsEditable(orden)) {
      enqueueSnackbar('No se puede eliminar una orden ya facturada', { variant: 'warning' });
      return;
    }
    const apiId = idOrdenTrabajoApi(orden);
    if (!apiId) {
      enqueueSnackbar('La orden no tiene identificador válido', { variant: 'warning' });
      return;
    }
    if (!window.confirm('¿Eliminar esta orden de trabajo?')) return;
    try {
      await ordenReparacionService.deleteOrden(apiId);
      enqueueSnackbar('Orden eliminada', { variant: 'success' });
      fetchOrdenes(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al eliminar'), { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Órdenes de trabajo
            </Typography>
            <Tooltip title="Insert o F2">
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={goNuevo}
                sx={{
                  borderRadius: 2,
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': { boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)' },
                }}
              >
                Nueva orden de trabajo
              </Button>
            </Tooltip>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  placeholder="Buscar por cliente, número o detalle..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Estado"
                  value={filtroEstado}
                  onChange={(e) => {
                    setFiltroEstado(e.target.value as EstadoOtFiltro);
                    setPage(0);
                  }}
                  sx={fieldSx}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {ESTADOS_OT.map((e) => (
                    <MenuItem key={e} value={e}>
                      {e}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  fullWidth
                  label="Fecha desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  fullWidth
                  label="Fecha hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={fechaDesde ? { min: fechaDesde } : undefined}
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
          </Box>

          {loading ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              Cargando...
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Orden</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Unidad</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Detalle</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordenes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                          No hay órdenes
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    ordenes.map((o) => {
                      const apiId = idOrdenTrabajoApi(o);
                      const editable = ordenTrabajoEsEditable(o);
                      return (
                        <TableRow key={apiId || String(o.id)} hover>
                          <TableCell>
                            {o.serie} {o.numero}
                          </TableCell>
                          <TableCell>{formatFecha(o.fechaEmision)}</TableCell>
                          <TableCell>
                            {o.nroCliente}
                            {o.nombreCliente ? ` - ${o.nombreCliente}` : ''}
                          </TableCell>
                          <TableCell>{o.patente || '—'}</TableCell>
                          <TableCell>{o.detalleReparacion || '—'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={labelEstadoOrdenTrabajo(o)}
                              color={chipColorEstado(o)}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.75} justifyContent="center" alignItems="center">
                              <Tooltip title="Imprimir">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={pdfLoadingId === apiId}
                                    onClick={(e) => void handleImprimir(o, e)}
                                    sx={btnSx('#2196f3', '#1976d2')}
                                  >
                                    {pdfLoadingId === apiId ? (
                                      <CircularProgress size={16} color="inherit" />
                                    ) : (
                                      <PrintIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              {puedeFacturarOrdenTrabajo(o) && (
                                <Can permiso="ventas.facturas.nuevo">
                                <Tooltip title="Facturar orden de trabajo">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleFacturar(o, e)}
                                    sx={btnSx('#4caf50', '#388e3c')}
                                  >
                                    <ReceiptIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                </Can>
                              )}
                              <Tooltip title={editable ? 'Editar' : 'Orden facturada (solo lectura)'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!apiId}
                                    onClick={() => {
                                      if (!apiId) return;
                                      navigate(ROUTES.servicio.ordenesTrabajo.editar(apiId));
                                    }}
                                    sx={btnSx('#2196f3', '#1976d2')}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title={editable ? 'Eliminar' : 'No se puede eliminar una orden facturada'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={!editable || !apiId}
                                    onClick={() => handleDelete(o)}
                                    sx={btnSx('#f44336', '#d32f2f')}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Filas:"
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>
      {pdfUrl && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={() => {
            window.URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
          }}
          title={pdfTitle}
          documentId="orden-trabajo"
          documentType="informeCliente"
          downloadFileName="orden-trabajo.pdf"
        />
      )}
    </Box>
  );
};

export default OrdenesReparacion;
